import { Shield } from 'lucide-react';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import '../styles/marketing.css';
import '../styles/legal.css';

const EFFECTIVE_DATE = 'March 15, 2026';

const sections = [
  { id: 'introduction', title: 'Introduction' },
  { id: 'information-we-collect', title: 'Information We Collect' },
  { id: 'how-we-use', title: 'How We Use Your Information' },
  { id: 'hipaa', title: 'HIPAA Compliance & PHI' },
  { id: 'data-sharing', title: 'Data Sharing & Third Parties' },
  { id: 'data-security', title: 'Data Security' },
  { id: 'data-retention', title: 'Data Retention & Deletion' },
  { id: 'your-rights', title: 'Your Rights' },
  { id: 'cookies', title: 'Cookies & Tracking' },
  { id: 'children', title: "Children's Privacy" },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'contact', title: 'Contact Information' },
];

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <MarketingNav />

      <section className="legal-hero">
        <div className="legal-hero-badge">
          <Shield size={14} />
          Effective {EFFECTIVE_DATE}
        </div>
        <h1>Privacy Policy</h1>
        <p className="legal-hero-subtitle">
          How Amplify by CareTracker collects, uses, and protects your personal
          and clinical information.
        </p>
      </section>

      <div className="legal-layout">
        <nav className="legal-toc">
          <h3>Contents</h3>
          <ol>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="legal-content">
          {/* 1. Introduction */}
          <section id="introduction" className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Harris CareTracker, a division of Harris Computer Systems
              (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or
              &quot;our&quot;), operates the Amplify by CareTracker platform
              (the &quot;Service&quot;), a HIPAA-compliant, medical-grade AI
              platform for recording, transcribing, and structuring clinical
              appointments.
            </p>
            <p>
              This Privacy Policy describes how we collect, use, disclose, and
              safeguard your information when you use our Service, including our
              web application, mobile applications (iOS and Android), and related
              services. By using the Service, you agree to the collection and
              use of information in accordance with this policy.
            </p>
            <p>
              We are committed to protecting the privacy and security of all
              personal information and Protected Health Information (PHI)
              entrusted to us by healthcare organizations, practitioners, and
              their patients.
            </p>
          </section>

          {/* 2. Information We Collect */}
          <section id="information-we-collect" className="legal-section">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Account Information</h3>
            <p>When you register for the Service, we collect:</p>
            <ul>
              <li>Full name, email address, and phone number</li>
              <li>Professional credentials (medical license, NPI, DEA number)</li>
              <li>Organization and practice information</li>
              <li>Role and access permissions</li>
              <li>Authentication credentials (encrypted)</li>
            </ul>

            <h3>2.2 Clinical Data (Protected Health Information)</h3>
            <p>
              In the course of providing the Service, we process the following
              clinical data on behalf of your healthcare organization:
            </p>
            <ul>
              <li>Audio recordings of clinical appointments</li>
              <li>Transcriptions generated from audio recordings</li>
              <li>
                Clinical summaries including chief complaint, history of present
                illness, review of systems, physical examination, assessment, and
                plan
              </li>
              <li>Patient demographic information</li>
              <li>
                Diagnostic codes (ICD-10) and procedure codes (CPT) generated
                during clinical coding
              </li>
              <li>Medical notes and clinical documentation</li>
            </ul>
            <div className="legal-info-box">
              <p>
                <strong>Important:</strong> All clinical data is classified as
                Protected Health Information (PHI) under HIPAA and is handled in
                accordance with our Business Associate Agreement (BAA) with your
                healthcare organization.
              </p>
            </div>

            <h3>2.3 Device & Usage Data</h3>
            <p>We automatically collect certain technical information:</p>
            <ul>
              <li>IP address, browser type, and operating system</li>
              <li>Device identifiers and platform (web, iOS, Android)</li>
              <li>Access times, pages viewed, and feature usage patterns</li>
              <li>Error logs and performance data</li>
            </ul>

            <h3>2.4 Biometric Data</h3>
            <p>
              On mobile devices, Amplify supports biometric authentication (Face
              ID, Touch ID, fingerprint). Biometric data is processed locally on
              your device using the operating system&apos;s secure enclave and is
              never transmitted to or stored on our servers.
            </p>
          </section>

          {/* 3. How We Use Your Information */}
          <section id="how-we-use" className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>

            <h3>3.1 Clinical Processing</h3>
            <ul>
              <li>
                Transcribing audio recordings using Azure Speech-to-Text with
                speaker diarization
              </li>
              <li>
                Generating clinical summaries using AI language models (Azure
                OpenAI)
              </li>
              <li>
                Recommending diagnostic (ICD-10) and procedure (CPT) codes using
                IMO Health&apos;s medical coding API
              </li>
              <li>Producing medical notes for physician review and signing</li>
            </ul>

            <h3>3.2 Service Operation</h3>
            <ul>
              <li>Providing, maintaining, and improving the Service</li>
              <li>
                Authenticating users and managing role-based access controls
              </li>
              <li>Processing subscription billing and usage tracking</li>
              <li>
                Providing customer support and responding to your requests
              </li>
            </ul>

            <h3>3.3 Analytics & Quality Improvement</h3>
            <ul>
              <li>
                Monitoring AI service usage and costs for operational efficiency
              </li>
              <li>
                Analyzing platform performance and identifying areas for
                improvement
              </li>
              <li>
                Generating anonymized, aggregated statistics (no individual PHI
                is used for analytics)
              </li>
            </ul>

            <h3>3.4 Compliance & Safety</h3>
            <ul>
              <li>
                Maintaining comprehensive audit trails for HIPAA compliance
              </li>
              <li>Detecting and preventing unauthorized access or misuse</li>
              <li>Fulfilling legal and regulatory obligations</li>
            </ul>
          </section>

          {/* 4. HIPAA Compliance */}
          <section id="hipaa" className="legal-section">
            <h2>4. HIPAA Compliance & Protected Health Information</h2>
            <p>
              Amplify by CareTracker is designed and operated as a
              HIPAA-compliant platform. We act as a Business Associate under
              HIPAA when processing PHI on behalf of Covered Entities (your
              healthcare organization).
            </p>

            <h3>4.1 Business Associate Agreement (BAA)</h3>
            <p>
              We execute a Business Associate Agreement with each healthcare
              organization before processing any PHI. The BAA governs our
              obligations regarding the use, disclosure, and safeguarding of
              PHI.
            </p>

            <h3>4.2 Minimum Necessary Standard</h3>
            <p>
              We apply the HIPAA Minimum Necessary standard, limiting PHI access
              to the minimum amount necessary to accomplish the intended purpose.
              Role-based access controls with over 50 granular permissions ensure
              that users only access data relevant to their clinical role.
            </p>

            <h3>4.3 PHI Safeguards</h3>
            <ul>
              <li>
                <strong>Administrative:</strong> Workforce training, access
                management policies, incident response procedures
              </li>
              <li>
                <strong>Physical:</strong> Data hosted in HIPAA-compliant
                Microsoft Azure data centers with SOC 2 Type II certification
              </li>
              <li>
                <strong>Technical:</strong> Encryption at rest and in transit
                (AES-256/TLS 1.2+), access logging, automatic session timeouts,
                multi-factor authentication
              </li>
            </ul>

            <h3>4.4 No AI Training on PHI</h3>
            <div className="legal-info-box">
              <p>
                <strong>We do not use your clinical data to train AI models.</strong>{' '}
                PHI processed through the Service is used solely for the purpose
                of providing clinical documentation services to your
                organization. Audio recordings, transcripts, and clinical
                summaries are never used for model training, research, or any
                purpose beyond your clinical workflow.
              </p>
            </div>
          </section>

          {/* 5. Data Sharing */}
          <section id="data-sharing" className="legal-section">
            <h2>5. Data Sharing & Third Parties</h2>
            <p>
              We do not sell, rent, or trade your personal information or PHI. We
              share data only as described below:
            </p>

            <h3>5.1 Service Providers (Sub-processors)</h3>
            <p>
              We use the following third-party service providers to operate the
              Service, each bound by data processing agreements:
            </p>
            <ul>
              <li>
                <strong>Microsoft Azure:</strong> Cloud hosting, AI services
                (Speech-to-Text, OpenAI, Language Understanding), blob storage,
                and key vault for secrets management
              </li>
              <li>
                <strong>IMO Health:</strong> Medical terminology and clinical
                coding services (ICD-10/CPT code recommendations)
              </li>
              <li>
                <strong>EHR Systems:</strong> When connected mode is enabled, we
                exchange clinical data with your organization&apos;s Electronic
                Health Record system (e.g., CareTracker EHR) per your
                organization&apos;s configuration
              </li>
            </ul>

            <h3>5.2 Legal Requirements</h3>
            <p>
              We may disclose information when required by law, regulation, legal
              process, or governmental request, or when we believe in good faith
              that disclosure is necessary to protect our rights, your safety, or
              the safety of others.
            </p>

            <h3>5.3 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, your
              information may be transferred as part of that transaction. We will
              notify you of any such change and any choices you may have
              regarding your information.
            </p>
          </section>

          {/* 6. Data Security */}
          <section id="data-security" className="legal-section">
            <h2>6. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your
              data:
            </p>
            <ul>
              <li>
                <strong>Encryption at rest:</strong> All data stored using
                AES-256 encryption in Azure-managed storage
              </li>
              <li>
                <strong>Encryption in transit:</strong> All communications
                secured with TLS 1.2 or higher
              </li>
              <li>
                <strong>Access controls:</strong> Role-based access control
                (RBAC) with over 50 granular permissions and Redis-cached
                permission checks
              </li>
              <li>
                <strong>Secrets management:</strong> Credentials stored in Azure
                Key Vault with managed identity access
              </li>
              <li>
                <strong>Audit logging:</strong> Comprehensive audit trails for
                all PHI access and modifications
              </li>
              <li>
                <strong>Screenshot protection:</strong> Mobile apps prevent
                screen capture of clinical data
              </li>
              <li>
                <strong>Session management:</strong> JWT-based authentication
                with automatic token rotation and session timeouts
              </li>
              <li>
                <strong>Multi-tenant isolation:</strong> Row-level security (RLS)
                ensures organizations cannot access each other&apos;s data
              </li>
            </ul>
          </section>

          {/* 7. Data Retention */}
          <section id="data-retention" className="legal-section">
            <h2>7. Data Retention & Deletion</h2>
            <p>
              We retain your information for as long as your account is active or
              as needed to provide the Service. Specific retention periods:
            </p>
            <ul>
              <li>
                <strong>Clinical data (PHI):</strong> Retained per your
                organization&apos;s retention policy and applicable state and
                federal medical record retention requirements (typically 7–10
                years)
              </li>
              <li>
                <strong>Audio recordings:</strong> Retained per your
                organization&apos;s policy; can be deleted upon request after
                transcription is complete
              </li>
              <li>
                <strong>Account data:</strong> Retained for the duration of the
                service agreement, then deleted within 90 days of termination
              </li>
              <li>
                <strong>Audit logs:</strong> Retained for a minimum of 6 years
                per HIPAA requirements
              </li>
              <li>
                <strong>Usage analytics:</strong> Retained for 2 years for
                billing and operational purposes
              </li>
            </ul>
            <p>
              We use soft deletes for clinical data to maintain audit trail
              integrity. When data is permanently deleted, it is removed from all
              active systems and backups within 30 days.
            </p>
          </section>

          {/* 8. Your Rights */}
          <section id="your-rights" className="legal-section">
            <h2>8. Your Rights</h2>

            <h3>8.1 HIPAA Rights (for patients)</h3>
            <p>
              Under HIPAA, patients have the right to access, amend, and receive
              an accounting of disclosures of their PHI. These requests should be
              directed to your healthcare provider (the Covered Entity), who will
              coordinate with us as necessary.
            </p>

            <h3>8.2 Account Holders</h3>
            <p>As a user of the Service, you have the right to:</p>
            <ul>
              <li>
                <strong>Access:</strong> Request a copy of the personal
                information we hold about you
              </li>
              <li>
                <strong>Correction:</strong> Request correction of inaccurate
                personal information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your personal
                information, subject to legal retention requirements
              </li>
              <li>
                <strong>Portability:</strong> Request your data in a structured,
                commonly used format
              </li>
              <li>
                <strong>Opt-out:</strong> Opt out of non-essential communications
              </li>
            </ul>

            <h3>8.3 California Residents (CCPA/CPRA)</h3>
            <p>
              California residents have additional rights under the California
              Consumer Privacy Act, including the right to know what personal
              information is collected, the right to delete, and the right to
              opt-out of the sale of personal information. We do not sell
              personal information.
            </p>

            <h3>8.4 Nevada Residents</h3>
            <p>
              Nevada residents may opt out of the sale of personal information by
              contacting us at{' '}
              <a href="mailto:sales@harriscaretracker.com">
                sales@harriscaretracker.com
              </a>
              . We do not currently sell personal information.
            </p>

            <p>
              To exercise any of these rights, contact us using the information
              provided in Section 12 below.
            </p>
          </section>

          {/* 9. Cookies */}
          <section id="cookies" className="legal-section">
            <h2>9. Cookies & Tracking Technologies</h2>
            <p>
              Our web application uses the following cookies and tracking
              technologies:
            </p>
            <ul>
              <li>
                <strong>Essential cookies:</strong> Required for authentication,
                session management, and security. These cannot be disabled.
              </li>
              <li>
                <strong>Analytics cookies:</strong> We use Google Analytics to
                understand how users interact with our marketing pages. No
                analytics tracking occurs within the clinical workspace or when
                processing PHI.
              </li>
              <li>
                <strong>Preference cookies:</strong> Remember your settings and
                preferences (e.g., theme, language).
              </li>
            </ul>
            <p>
              Our mobile applications do not use cookies. They use secure
              on-device storage (Capacitor Preferences) for authentication tokens
              and user preferences.
            </p>
          </section>

          {/* 10. Children's Privacy */}
          <section id="children" className="legal-section">
            <h2>10. Children&apos;s Privacy</h2>
            <p>
              The Service is designed for use by licensed healthcare
              professionals and authorized clinical staff. We do not knowingly
              collect personal information from individuals under the age of 18
              as account holders. If we become aware that we have collected
              personal information from a minor without parental consent, we will
              take steps to delete that information.
            </p>
            <p>
              Note: Clinical data about minor patients may be processed as PHI
              under the direction of the healthcare organization, in compliance
              with applicable laws including HIPAA and state-specific minor
              consent regulations.
            </p>
          </section>

          {/* 11. Changes */}
          <section id="changes" className="legal-section">
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices, technologies, legal requirements, or
              other factors. We will notify you of material changes by:
            </p>
            <ul>
              <li>Posting the updated policy on this page with a new effective date</li>
              <li>
                Sending an email notification to the administrator of your
                organization&apos;s account
              </li>
              <li>Displaying a notice within the Service upon your next login</li>
            </ul>
            <p>
              Your continued use of the Service after any changes constitutes
              your acceptance of the updated policy.
            </p>
          </section>

          {/* 12. Contact */}
          <section id="contact" className="legal-section">
            <h2>12. Contact Information</h2>
            <p>
              If you have questions about this Privacy Policy, wish to exercise
              your privacy rights, or need to report a privacy concern, please
              contact us:
            </p>
            <div className="legal-contact-box">
              <h3>Harris CareTracker — Amplify Privacy</h3>
              <p>
                <strong>Address:</strong> 2429 Military Rd, Suite 300, Niagara
                Falls, NY 14304
              </p>
              <p>
                <strong>Phone:</strong>{' '}
                <a href="tel:+18555284357">(855) 528-4357</a>
              </p>
              <p>
                <strong>Email:</strong>{' '}
                <a href="mailto:sales@harriscaretracker.com">
                  sales@harriscaretracker.com
                </a>
              </p>
              <p>
                <strong>Website:</strong>{' '}
                <a
                  href="https://harriscaretracker.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  harriscaretracker.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
};

export default PrivacyPolicy;
